import { Component, Input, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { filter, take, switchMap, catchError, of, map } from 'rxjs';

import { InstantEstimateComponent } from './instant-estimate.component';
import { InstantEstimateService } from './instant-estimate.service';
import { InstantEstimateConfig } from './instant-estimate.models';
import { TitleService } from '_services/title.service';
import { UserService } from '_services/user.service';
import { UnivPrefsService } from '_services/univ-prefs.service';

@Component({
  selector: 'app-instant-estimate-page',
  standalone: true,
  imports: [CommonModule, InstantEstimateComponent],
  templateUrl: './instant-estimate-page.component.html',
  styleUrl: './instant-estimate-page.component.css',
})
export class InstantEstimatePageComponent {
  private readonly titleService = inject(TitleService);
  private readonly estimateService = inject(InstantEstimateService);
  private readonly userService = inject(UserService);
  readonly ups = inject(UnivPrefsService);

  readonly config = signal<InstantEstimateConfig | null>(null);
  readonly loadError = signal<string | null>(null);
  readonly schoolIdSignal = signal('');

  readonly accentColor = computed(
    () => this.ups.preferences()?.headerColor || '#002999'
  );
  readonly panelColor = computed(
    () => this.ups.preferences()?.panelColor || '#ffffff'
  );

  /** Hide school name when branded header logo is already showing. */
  readonly showSchoolEyebrow = computed(() => !this.ups.logoURL());

  @Input()
  set schoolId(schoolId: string) {
    const id = (schoolId || '').toLowerCase().trim();
    this.schoolIdSignal.set(id);
    this.titleService.setTitle('Instant Estimate', id);
    this.userService.schoolId = id;

    const config = this.estimateService.getConfig(id);
    this.config.set(config);

    if (!config) {
      this.loadError.set(
        'Instant estimate is not available for this school yet.'
      );
      return;
    }

    const validation = this.estimateService.getValidationErrors(config);
    if (validation.length) {
      this.loadError.set('Instant estimate data for this school is invalid.');
      return;
    }

    this.loadError.set(null);
    this.loadPreferences(id);
  }

  private loadPreferences(schoolId: string): void {
    this.userService.user
      .pipe(
        filter((user) => !!user),
        take(1),
        switchMap(() =>
          this.ups.getPreferences$(schoolId, this.userService.isAdmin).pipe(
            map((ok) => !!ok),
            catchError(() => of(false))
          )
        )
      )
      .subscribe();
  }
}
